import React, { memo, useMemo } from 'react';
import styled from 'styled-components';
import { useMutation } from '@apollo/client';
import { batch } from 'react-redux';
import { workspacePropertySelector } from '../../../redux/selectors/workspace';
import { extractLoadedAnalyses } from '../../../redux/selectors/analyses';
import {
  cancelEditingForCurrentLoadedAnalysesAction,
  enableEditingForCurrentLoadedAnalysesAction,
} from '../../../redux/slices/analyses';
import * as WorkspaceOptionsUI from './WorkspaceOptionsUI';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import {
  IWorkspaceEditMutation,
  IWorkspaceEditMutationVariables,
} from '../../../utils/apollo/graphqlSchema';
import { areAllAnalysesLoadedSelector } from '../../../redux/selectors/loadingAnalyses';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { updateWorkspaces } from '../../../redux/slices/workspaces';
import { WORKSPACE_EDIT } from '../../../utils/apollo/workspaces';
import {
  generateWsAnalysisInput,
  useLoadWorkspaceAnalyses,
} from '../../../utils/hooks/workspaces';
import {
  IWorkspaceSliceRow,
  populateWorkspace,
  workspaceEditActivate,
  workspaceEditCancel,
  workspaceEditSave,
} from '../../../redux/slices/workspace';
import { assertCondition } from '../../../utils/types';
import ActionsMenu from '../../ActionsMenu';
import { PrimaryButton, SecondaryButton } from '../../Button';
import { ModalSliceType, openModal } from '../../../redux/slices/modal';
import {
  workspaceUpdated,
  workspaceViewed,
} from '../../../utils/analytics/events/workspace/events';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { SingleSelectOption } from '../../Dropdown/SingleSelect';

const StyledActionsMenu = styled(ActionsMenu)`
  margin-right: 8px;
`;

export default memo(function WorkspaceOptions() {
  const dispatch = useAppDispatch();
  const { track } = useAnalytics();

  const workspaceId = useAppSelector((state) =>
    workspacePropertySelector(state, 'id'),
  );
  const name = useAppSelector((state) =>
    workspacePropertySelector(state, 'name'),
  );
  const description = useAppSelector((state) =>
    workspacePropertySelector(state, 'description'),
  );
  const isWorkspaceInEditingMode = useAppSelector((state) =>
    workspacePropertySelector(state, 'isEditing'),
  );
  const workspaceRows: IWorkspaceSliceRow[] = useAppSelector((state) =>
    workspacePropertySelector(state, 'rows'),
  );

  const workspaceAnalysesIds = useMemo(() => {
    if (!workspaceRows?.length) {
      return [];
    }

    return workspaceRows.reduce<string[]>(
      (accumulator, { analysesIds }) => [...accumulator, ...analysesIds],
      [],
    );
  }, [workspaceRows]);

  const currentWorkspaceMemoizedAnalysesSelector = useMemo(
    extractLoadedAnalyses,
    [],
  );

  const currentWorkspaceLoadedAnalyses = useAppSelector((state) =>
    currentWorkspaceMemoizedAnalysesSelector(state, workspaceAnalysesIds),
  );

  const areWorkspaceAnalysesLoaded = useAppSelector(
    areAllAnalysesLoadedSelector,
  );

  const loadWorkspaceAnalyses = useLoadWorkspaceAnalyses();

  /* workspace edit mutation configuration */
  const [editWorkspace, { loading: isLoading }] = useMutation<
    IWorkspaceEditMutation,
    IWorkspaceEditMutationVariables
  >(WORKSPACE_EDIT);

  const enableEditMode = () => {
    batch(() => {
      dispatch(workspaceEditActivate());
      dispatch(
        enableEditingForCurrentLoadedAnalysesAction(workspaceAnalysesIds),
      );
    });
  };
  const cancelEditMode = () => {
    batch(() => {
      dispatch(workspaceEditCancel());
      dispatch(
        cancelEditingForCurrentLoadedAnalysesAction(workspaceAnalysesIds),
      );
    });
  };
  const onDeleteWorkspace = () => {
    dispatch(openModal({ type: ModalSliceType.WorkspaceDelete }));
  };

  const editWorkspaceInfoModalHandler = () =>
    dispatch(openModal({ type: ModalSliceType.WorkspaceEdit }));

  const persistWorkspace = async () => {
    const rows = workspaceRows.map((row) => {
      const analyses = row.analysesIds.map((analysisId) => {
        const currentAnalysis = currentWorkspaceLoadedAnalyses.find(
          (loadedAnalysis: any) => loadedAnalysis.current.id === analysisId,
        );

        assertCondition(currentAnalysis?.metadata);

        return generateWsAnalysisInput({
          analysisId: currentAnalysis?.metadata?.analysisId,
          display: currentAnalysis?.metadata?.display,
          description: currentAnalysis?.metadata?.description,
          currentAnalysis,
        });
      });

      return {
        analyses,
      };
    });

    const { data } = await editWorkspace({
      variables: {
        input: {
          comment: '',
          description,
          name,
          rows,
          workspaceId,
        },
      },
    });

    if (data?.workspaceEdit) {
      const { errors, entity } = data.workspaceEdit;

      if (printGraphqlErrors(errors)) {
        return;
      }

      if (entity) {
        track(workspaceUpdated(entity));
        // we reload the workspace so this constitutes as a new view
        track(workspaceViewed(entity));

        batch(() => {
          dispatch(workspaceEditSave());
          dispatch(populateWorkspace(entity));
          dispatch(
            updateWorkspaces({
              id: entity.id,
              name: entity.name,
            }),
          );
          loadWorkspaceAnalyses(entity.rows);
        });
      }
    }
  };

  if (isWorkspaceInEditingMode) {
    return (
      <>
        <ActionsMenu disabled={!areWorkspaceAnalysesLoaded}>
          <SingleSelectOption
            onClick={editWorkspaceInfoModalHandler}
            iconName="pencil"
            label={'Edit Workspace Info...'}
          />
        </ActionsMenu>
        <WorkspaceOptionsUI.ButtonsWrapper>
          <SecondaryButton
            type="button"
            onClick={cancelEditMode}
            isOutlined
            trackContext="workspaces"
            trackAction="cancel"
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="button"
            onClick={persistWorkspace}
            loading={isLoading}
            trackContext="workspaces"
            trackAction="save"
          >
            Save
          </PrimaryButton>
        </WorkspaceOptionsUI.ButtonsWrapper>
      </>
    );
  }

  return (
    <StyledActionsMenu disabled={!areWorkspaceAnalysesLoaded}>
      <SingleSelectOption
        onClick={enableEditMode}
        iconName="pencil"
        label={'Edit Workspace'}
      />
      <SingleSelectOption
        onClick={onDeleteWorkspace}
        iconName="trash"
        label={'Delete Workspace'}
      />
    </StyledActionsMenu>
  );
});
